import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { companiesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Loader2, Save, Upload, ArrowLeft, FileText, ImageIcon } from 'lucide-react';

const LetterheadSettings = () => {
  const { companyId } = useParams();
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lh, setLh] = useState({
    header_text: '', footer_text: '', address_block: '',
    authorized_signatory_name: '', authorized_signatory_designation: '',
    watermark_text: '', primary_color: '#0f172a',
  });
  const [uploading, setUploading] = useState(null);
  const [refresh, setRefresh] = useState(0);

  // Role guard — only admin roles
  const allowedRole = !user || ['super_admin', 'hr_admin'].includes(user?.role);

  useEffect(() => {
    if (!allowedRole) return;
    (async () => {
      try {
        const r = await companiesAPI.getById(companyId);
        setCompany(r.data);
        setLh({
          header_text: r.data.letterhead?.header_text || r.data.name || '',
          footer_text: r.data.letterhead?.footer_text || r.data.address || '',
          address_block: r.data.letterhead?.address_block || r.data.address || '',
          authorized_signatory_name: r.data.letterhead?.authorized_signatory_name || '',
          authorized_signatory_designation: r.data.letterhead?.authorized_signatory_designation || '',
          watermark_text: r.data.letterhead?.watermark_text || '',
          primary_color: r.data.letterhead?.primary_color || '#0f172a',
        });
      } catch (e) { toast.error('Failed to load company'); }
      finally { setLoading(false); }
    })();
  }, [companyId]);

  const save = async () => {
    setSaving(true);
    try {
      await companiesAPI.updateLetterhead(companyId, lh);
      toast.success('Letterhead saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const uploadFile = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      if (kind === 'logo') await companiesAPI.uploadLogo(companyId, file);
      else await companiesAPI.uploadSignature(companyId, file, kind);
      toast.success(`${kind} uploaded`);
      setRefresh(r => r + 1);
      const r = await companiesAPI.getById(companyId);
      setCompany(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(null); }
  };

  if (loading || !company) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!allowedRole) return <Navigate to="/" replace />;

  const logoSrc = company.has_logo ? `${companiesAPI.logoUrl(companyId)}&r=${refresh}` : null;
  const sigSrc = company.signature_file_id ? `${companiesAPI.signatureUrl(companyId, 'signature')}&r=${refresh}` : null;
  const stampSrc = company.stamp_file_id ? `${companiesAPI.signatureUrl(companyId, 'stamp')}&r=${refresh}` : null;

  return (
    <div className="space-y-6" data-testid="letterhead-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/companies"><Button variant="ghost" size="icon"><ArrowLeft size={18} /></Button></Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><FileText />Letterhead: {company.name}</h1>
            <p className="text-muted-foreground text-sm">Customize the branding for offer letters, salary certificates and future documents</p>
          </div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-letterhead-btn">{saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}Save</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Settings */}
        <Card>
          <CardHeader><CardTitle className="text-base">Header & Footer</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div><Label>Header Text</Label><Input value={lh.header_text} onChange={(e) => setLh({...lh, header_text: e.target.value})} placeholder={company.name} data-testid="lh-header" /></div>
            <div><Label>Address Block (appears under header)</Label><Textarea value={lh.address_block} onChange={(e) => setLh({...lh, address_block: e.target.value})} rows={2} data-testid="lh-address" /></div>
            <div><Label>Footer Text</Label><Input value={lh.footer_text} onChange={(e) => setLh({...lh, footer_text: e.target.value})} placeholder="Confidential" data-testid="lh-footer" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Primary Color</Label><Input type="color" value={lh.primary_color} onChange={(e) => setLh({...lh, primary_color: e.target.value})} data-testid="lh-color" /></div>
              <div><Label>Watermark (optional)</Label><Input value={lh.watermark_text} onChange={(e) => setLh({...lh, watermark_text: e.target.value})} placeholder="e.g. DRAFT" data-testid="lh-watermark" /></div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Authorized Signatory</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div><Label>Name</Label><Input value={lh.authorized_signatory_name} onChange={(e) => setLh({...lh, authorized_signatory_name: e.target.value})} data-testid="lh-signatory-name" /></div>
            <div><Label>Designation</Label><Input value={lh.authorized_signatory_designation} onChange={(e) => setLh({...lh, authorized_signatory_designation: e.target.value})} data-testid="lh-signatory-desig" /></div>
          </CardContent>
        </Card>

        {/* Image uploads */}
        {[
          { kind: 'logo', label: 'Company Logo', src: logoSrc, hint: 'Recommended: 200×200 PNG, max 2 MB' },
          { kind: 'signature', label: 'Signature', src: sigSrc, hint: 'Recommended: 400×150 transparent PNG, max 1 MB' },
          { kind: 'stamp', label: 'Stamp / Seal', src: stampSrc, hint: 'Recommended: 200×200 transparent PNG, max 1 MB' },
        ].map(({ kind, label, src, hint }) => (
          <Card key={kind}>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><ImageIcon size={16} />{label}</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 border rounded bg-muted/30 flex items-center justify-center overflow-hidden">
                  {src ? <img src={src} alt={kind} className="w-full h-full object-contain" data-testid={`preview-${kind}`} /> : <ImageIcon className="text-muted-foreground" />}
                </div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground mb-2">{hint}</p>
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadFile(kind, e.target.files?.[0])} data-testid={`upload-${kind}`} />
                    <Button variant="outline" asChild disabled={uploading === kind}>
                      <span>{uploading === kind ? <Loader2 size={14} className="animate-spin mr-2" /> : <Upload size={14} className="mr-2" />}{src ? 'Replace' : 'Upload'}</span>
                    </Button>
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Preview */}
      <Card>
        <CardHeader><CardTitle className="text-base">Preview</CardTitle></CardHeader>
        <CardContent>
          <div className="border rounded-lg p-8 bg-white text-black relative" style={{ minHeight: 320 }}>
            <div className="absolute top-0 left-0 right-0 h-2" style={{ background: lh.primary_color }} />
            {lh.watermark_text && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-7xl font-bold opacity-10 rotate-12">{lh.watermark_text}</span>
              </div>
            )}
            <div className="relative flex items-start gap-4 pb-4 border-b">
              {logoSrc && <img src={logoSrc} alt="logo" className="h-16 w-16 object-contain" />}
              <div className="flex-1">
                <h2 className="text-xl font-bold" style={{ color: lh.primary_color }}>{lh.header_text || company.name}</h2>
                <p className="text-xs text-gray-600 whitespace-pre-line">{lh.address_block}</p>
              </div>
            </div>
            <div className="relative mt-6 text-sm text-gray-700">
              <p className="font-semibold mb-2">SAMPLE DOCUMENT TITLE</p>
              <p>This is how your letterhead will look. The body content of the actual document (offer letter / salary certificate) will appear here with proper paragraphs and formatting.</p>
            </div>
            <div className="relative mt-12 flex items-end justify-between">
              <div>
                {sigSrc && <img src={sigSrc} alt="sig" className="h-12 object-contain mb-1" />}
                <p className="text-sm font-medium">{lh.authorized_signatory_name || '— Authorized Signatory —'}</p>
                <p className="text-xs text-gray-600">{lh.authorized_signatory_designation}</p>
                <p className="text-xs text-gray-600">{company.name}</p>
              </div>
              {stampSrc && <img src={stampSrc} alt="stamp" className="h-16 w-16 object-contain opacity-80" />}
            </div>
            {lh.footer_text && <p className="absolute bottom-2 left-0 right-0 text-center text-xs text-gray-500">{lh.footer_text}</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LetterheadSettings;
